'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useDebouncedCallback } from 'use-debounce';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/lib/store/hooks';
import { updateSearchParams } from '@/app/lib/store/search-params-slice';
import Search from './search';

export default function SearchContainer() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  const storedSearchParams = useAppSelector(
    state => state.searchParams.params,
    (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
  );

  const { query } = storedSearchParams;
  const dispatch = useAppDispatch();

  const [value, setValue] = useState(query ? decodeURI(query) : '');

  const dispatchSearchParams = useCallback(
    (query: string) => {
      dispatch(updateSearchParams({ ...storedSearchParams, query, page: '1' }));
    },
    [storedSearchParams, dispatch]
  );

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    const oldQuery = params.get('query');

    if (oldQuery === encodeURI(value) || (oldQuery === null && !value)) return;

    params.delete('query');

    if (value) {
      params.set('query', encodeURI(value));
      params.set('page', '1');

      dispatchSearchParams(encodeURI(value));
    } else {
      dispatchSearchParams('');
    }
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [value, dispatch, replace, pathname, searchParams, dispatchSearchParams]);

  const handleSearch = useDebouncedCallback(term => {
    setValue(term);
  }, 300);

  const handleClear = () => {
    if (!value) return;
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    params.delete('query');

    replace(`${pathname}?${params.toString()}`);
    setValue('');
    dispatchSearchParams('');
  };

  return (
    <Search
      value={value}
      setValue={setValue}
      onSearch={handleSearch}
      onClear={handleClear}
    />
  );
}
